import { TFunction } from 'i18next'
import React, { useCallback, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { ImageNotSupported } from '@mui/icons-material'
import { Box, Button, Card, CardContent, Divider, Skeleton, Stack, Typography } from '@mui/material'
import { Theme } from '@mui/material/styles'

import { useGetBlueprintFamilyEnrichmentQuery } from '../../../../api/blueprintFamilies/enrichment'
import { BlueprintFamilyExampleImage, resolveExampleImageGameName } from '../../../../api/blueprintFamilies/exampleImages'
import { LiveEventSourceCaption } from '../../../../components/LiveEventSourceCaption/LiveEventSourceCaption'
import MultiSlider from '../../../../components/MultiSlider/MultiSlider'
import { borderRadiusInset } from '../../../../themes/theme'
import { LockedGameSummary } from '../../../../types/LockedFeature'
import { useCurrentUserLanguage } from '../../../account/hooks/useCurrentUserLanguage'
import { useExampleImageGame } from '../../../live-events/hooks/useExampleImageGame'
import { useLockedGameDialog } from '../../../live-events/hooks/useLockedGameDialog'
import { BlueprintScreenshotLightbox } from '../BlueprintScreenshotLightbox'
import { sectionLabelSx } from '../helpers/typographyStyles'

interface BlueprintScreenshotsCardProps {
  blueprintId: string
}

// `gameAllowed` is `false` for a game outside the user's plan. Absent reads as allowed.
const isLockedImage = (image: BlueprintFamilyExampleImage) => image.gameAllowed === false

// Portrait galleries use the taller slide; this is also the max height the card
// ever grows to, so the loading skeleton reserves it to keep the loading and
// loaded states the same size and stop the page from wobbling as images resolve.
const SCREENSHOT_SLIDE_HEIGHT_PORTRAIT = 340
const SCREENSHOT_SLIDE_HEIGHT_LANDSCAPE = 300
// Room under a slide for the source caption: the `small` (42px) game icon the
// stacked `body2` caption renders next to its two text lines. Reserved by the
// skeleton too, so the card doesn't grow as the images resolve.
const SCREENSHOT_CAPTION_HEIGHT = 42
// Caption inset inside its tile, in theme spacing units.
const SCREENSHOT_CAPTION_PADDING_X = 1.5
const SCREENSHOT_CAPTION_PADDING_Y = 1
// Each slide is its own bordered tile (image on top, caption below), like the
// Data Glossary blueprint cards. 1px top + 1px bottom of border.
const SCREENSHOT_TILE_BORDER_HEIGHT = 2
// Compact height for the "no example images yet" empty state — that state has no
// slider to match, so it doesn't need to reserve the full slide height.
const SCREENSHOTS_PLACEHOLDER_MIN_HEIGHT = 132

interface ScreenshotSlideProps {
  image: BlueprintFamilyExampleImage
  index: number
  slideHeight: number
  // `lockedGame` is only passed for a locked image: the source game, with the icon and publisher the unlock dialog shows.
  onSelect: (event: React.SyntheticEvent, index: number, lockedGame?: LockedGameSummary) => void
  onLockedClick: (event: React.SyntheticEvent, game?: LockedGameSummary) => void
  onAspectLoad: (index: number, ratio: number) => void
  t: TFunction
  // Injected by `MultiSlider` via `cloneElement`: the `item` class plus the measured
  // slide `width` and horizontal `padding`. They have to reach the root DOM node, or
  // every slide falls back to its content width — and with a no-wrap caption under
  // the image, that is the caption's text length, so slides came out unequal and
  // long captions ran into their neighbours.
  className?: string
  style?: React.CSSProperties
}

// A single screenshot slide. Memoized so the slider only re-renders the slides
// whose props actually change.
const ScreenshotSlide: React.FC<ScreenshotSlideProps> = React.memo(
  ({ image, index, slideHeight, onSelect, onLockedClick, onAspectLoad, t, className, style }) => {
    const locked = isLockedImage(image)
    // Only a locked image needs the game here (for the unlock dialog); the caption below
    // already fetches it for every image, and RTK Query dedupes the two by args.
    const { lockedGame } = useExampleImageGame(image, locked)
    const handleLoad = (event: React.SyntheticEvent<HTMLImageElement>) => {
      const img = event.currentTarget
      if (!img.naturalWidth || !img.naturalHeight) return
      onAspectLoad(index, img.naturalWidth / img.naturalHeight)
    }
    return (
      <Box className={className} style={style} sx={{ minWidth: 0 }}>
        {/* Tile: image edge-to-edge on top, divider, caption below — the same anatomy as
          a Data Glossary blueprint card, so a screenshot reads as one framed unit
          with its attribution instead of a bare image with text floating under it.
          A hairline border rather than elevation: these already sit inside an
          elevated card, and stacked shadows read as clutter. */}
        <Box
          sx={(theme: Theme) => ({
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: borderRadiusInset,
            // Clips the square-cornered image and letterbox to the tile's rounded corners.
            overflow: 'hidden',
            backgroundColor: 'background.paper',
          })}
        >
          <Box
            component="button"
            type="button"
            onClick={(event) => onSelect(event, index, locked ? lockedGame : undefined)}
            aria-label={locked ? t('live-events:locked_game_tooltip') : t('market-analytics:detail_panel_screenshot_reference_label', { n: index + 1 })}
            sx={{ p: 0, border: 'none', background: 'none', cursor: 'pointer', display: 'block', width: '100%' }}
          >
            <Box
              component="img"
              src={image.url}
              alt={t('market-analytics:detail_panel_screenshot_reference_label', { n: index + 1 })}
              loading="lazy"
              draggable={false}
              onLoad={handleLoad}
              sx={{
                width: '100%',
                height: slideHeight,
                objectFit: 'contain',
                display: 'block',
                // Neutral gray letterbox, not `background.default`: that token is the page's own
                // background, so a portrait shot narrower than the slide used to blend straight
                // into the page instead of reading as its own contained image area.
                // `grey.300` (MUI's default gray scale, not the GR `lightGrayBackground` token used
                // elsewhere for chart tracks) reads a touch darker against the white card.
                backgroundColor: 'grey.300',
              }}
            />
          </Box>
          <Divider />
          {/* The caption sits outside the image button: it opens the event, which is a
            different destination from the lightbox the image itself opens. Same
            treatment as the Data Glossary gallery card — game icon, then the game and
            event names on their own lines — so the two surfaces attribute a shot the
            same way. */}
          <Box
            sx={{
              height: SCREENSHOT_CAPTION_HEIGHT,
              px: SCREENSHOT_CAPTION_PADDING_X,
              py: SCREENSHOT_CAPTION_PADDING_Y,
              boxSizing: 'content-box',
              minWidth: 0,
            }}
          >
            <LiveEventSourceCaption image={image} variant="body2" showGameIcon stacked onLockedClick={onLockedClick} />
          </Box>
        </Box>
      </Box>
    )
  }
)
ScreenshotSlide.displayName = 'ScreenshotSlide'

/**
 * "Example screenshots" card from the v14 mockup — the analyst-curated glossary
 * screenshots with a click-to-enlarge lightbox. Mirrors the catalogue side rail's
 * screenshot slider; RTK Query dedupes the enrichment request by cache key.
 */
export const BlueprintScreenshotsCard: React.FC<BlueprintScreenshotsCardProps> = ({ blueprintId }) => {
  const { t } = useTranslation()
  const userLanguage = useCurrentUserLanguage()
  const { openLockedGameDialog, lockedGameDialog } = useLockedGameDialog()

  // The curated screenshots don't depend on the segment, so the request carries no
  // scope: a segment change neither refetches them nor drops the gallery back to
  // the skeleton. Only the family id keys it, so `currentData` never shows the
  // previous family's images under the next one.
  const {
    currentData: enrichment,
    isFetching,
    isError,
    refetch,
  } = useGetBlueprintFamilyEnrichmentQuery({ blueprintFamilyId: blueprintId }, { skip: !blueprintId })

  // Keep the API order: it is the analyst-curated order (set in the LE Admin editor),
  // which the Data Glossary gallery shows too.
  const screenshots = useMemo(() => enrichment?.glossaryImages ?? [], [enrichment])
  const isLoadingScreenshots = isFetching && !enrichment
  const isScreenshotsError = isError && !enrichment && !isFetching

  // The lightbox is the deeper look a locked game doesn't get, so it only ever pages through the
  // open shots — otherwise paging from an open one would land on a locked one.
  const viewableImages = useMemo(() => screenshots.filter((image) => !isLockedImage(image)), [screenshots])

  // Position within `viewableImages`, not within `screenshots`.
  const [lightboxIndex, setLightboxIndex] = useState<number | null>(null)

  // Natural aspect ratio per loaded screenshot: a landscape-dominant gallery
  // shows up to two wide shots at a time, a portrait gallery shows up to four
  // (the card spans the full main column since the v14 feedback round).
  const [screenshotAspects, setScreenshotAspects] = useState<Record<number, number>>({})

  // Another family's gallery starts from scratch: reset during render rather than in
  // an effect, so its first paint doesn't reuse the previous layout or open lightbox.
  const [galleryBlueprintId, setGalleryBlueprintId] = useState(blueprintId)
  if (galleryBlueprintId !== blueprintId) {
    setGalleryBlueprintId(blueprintId)
    setScreenshotAspects({})
    setLightboxIndex(null)
  }

  // The index points into `viewableImages`; if that list shrinks or reorders under an open
  // lightbox (e.g. the enrichment refetches), close it rather than leave an empty dialog up.
  if (lightboxIndex !== null && !viewableImages[lightboxIndex]) {
    setLightboxIndex(null)
  }

  const { slidesPerView, slideHeight, hasSliderControls } = useMemo(() => {
    const loadedAspects = Object.values(screenshotAspects)
    const landscapeCount = loadedAspects.filter((ratio) => ratio > 1).length
    const isLandscapeGallery = loadedAspects.length > 0 && landscapeCount > loadedAspects.length / 2
    const perView = isLandscapeGallery ? Math.min(2, Math.max(1, screenshots.length)) : Math.min(4, Math.max(1, screenshots.length))
    return {
      slidesPerView: perView,
      slideHeight: isLandscapeGallery ? SCREENSHOT_SLIDE_HEIGHT_LANDSCAPE : SCREENSHOT_SLIDE_HEIGHT_PORTRAIT,
      hasSliderControls: screenshots.length > perView,
    }
  }, [screenshotAspects, screenshots])

  const handleScreenshotSelect = useCallback(
    (event: React.SyntheticEvent, index: number, lockedGame?: LockedGameSummary) => {
      const selected = screenshots[index]
      if (!selected) return
      // `MultiSlider` cancels `mousedown` for drag support, so the clicked slide never takes focus on its
      // own. Focus it here (read synchronously: `currentTarget` is cleared after the handler) so the
      // lightbox / unlock dialog hands focus back to the screenshot on close, not to a control the user
      // used in another section, which would scroll the page back to it.
      const trigger = event.currentTarget
      if (trigger instanceof HTMLElement) trigger.focus({ preventScroll: true })
      if (isLockedImage(selected)) {
        openLockedGameDialog(event, lockedGame ?? { name: resolveExampleImageGameName(selected, userLanguage) || selected.eventName || '' })
        return
      }
      setLightboxIndex(viewableImages.indexOf(selected))
    },
    [screenshots, viewableImages, openLockedGameDialog, userLanguage]
  )
  const handleScreenshotAspectLoad = useCallback((index: number, ratio: number) => {
    setScreenshotAspects((prev) => (prev[index] === ratio ? prev : { ...prev, [index]: ratio }))
  }, [])

  return (
    <Card>
      <CardContent>
        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
          <Typography sx={sectionLabelSx}>{t('market-analytics:detail_panel_screenshots_label')}</Typography>
          {screenshots.length > 0 ? (
            <Typography variant="caption" color="text.secondary">
              {t('market-analytics:detail_panel_screenshots_click_to_enlarge')}
            </Typography>
          ) : null}
        </Stack>
        {isLoadingScreenshots ? (
          <Skeleton
            variant="rounded"
            sx={(theme: Theme) => ({
              width: '100%',
              // Mirrors a loaded tile: image + 1px divider + padded caption + tile border.
              height: `calc(${SCREENSHOT_SLIDE_HEIGHT_PORTRAIT + 1 + SCREENSHOT_CAPTION_HEIGHT + SCREENSHOT_TILE_BORDER_HEIGHT}px + ${theme.spacing(
                SCREENSHOT_CAPTION_PADDING_Y * 2
              )})`,
              borderRadius: borderRadiusInset,
            })}
          />
        ) : screenshots.length > 0 ? (
          <MultiSlider
            count={slidesPerView}
            padding={8}
            showIndex={hasSliderControls}
            infinite={hasSliderControls}
            floatingButtons
            floatingIndex
            mediaHeight={slideHeight}
            ariaLabel={t('market-analytics:detail_panel_screenshots_label')}
          >
            {screenshots.map((image, idx) => (
              <ScreenshotSlide
                key={`${image.url}-${idx}`}
                image={image}
                index={idx}
                slideHeight={slideHeight}
                onSelect={handleScreenshotSelect}
                onLockedClick={openLockedGameDialog}
                onAspectLoad={handleScreenshotAspectLoad}
                t={t}
              />
            ))}
          </MultiSlider>
        ) : (
          <Stack
            role={isScreenshotsError ? 'alert' : undefined}
            alignItems="center"
            justifyContent="center"
            spacing={0.75}
            sx={(theme: Theme) => ({
              py: 3,
              minHeight: SCREENSHOTS_PLACEHOLDER_MIN_HEIGHT,
              borderRadius: borderRadiusInset,
              border: `1px dashed ${theme.palette.divider}`,
              backgroundColor: 'background.default',
              color: 'text.secondary',
            })}
          >
            {isScreenshotsError ? (
              <>
                <Typography variant="body2" color="warning.main">
                  {t('market-analytics:enrichment_screenshots_load_error')}
                </Typography>
                <Button variant="text" size="small" onClick={refetch}>
                  {t('market-analytics:retry')}
                </Button>
              </>
            ) : (
              <>
                <ImageNotSupported sx={{ fontSize: 28, opacity: 0.6 }} />
                <Typography variant="body2" color="text.secondary">
                  {t('market-analytics:enrichment_no_screenshots')}
                </Typography>
              </>
            )}
          </Stack>
        )}
      </CardContent>

      <BlueprintScreenshotLightbox
        images={viewableImages}
        index={lightboxIndex}
        onIndexChange={setLightboxIndex}
        onClose={() => setLightboxIndex(null)}
        ariaLabel={t('market-analytics:detail_panel_screenshots_label')}
        getImageAlt={(image) => t('market-analytics:detail_panel_screenshot_reference_label', { n: screenshots.indexOf(image) + 1 })}
      />
      {lockedGameDialog}
    </Card>
  )
}
